import { useState } from 'react'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  MAP_ACTION_DESCRIPTIONS,
  MapActionConfigItem,
  MapActionKey,
  MapActionsConfig,
  MapActionState,
} from '@ivy-monorepo/shared'
import Icon from '../../Icon'
import MapActionIcon from './MapActionIcon'
import MapActionCenterPill from './MapActionCenterPill'
import MapActionEditModal, { MAP_ACTION_STATE_OPTIONS } from './MapActionEditModal'
import MapActionsPhonePreview from './MapActionsPhonePreview'

type MapActionsEditorProps = {
  value: MapActionsConfig
  onChange: (value: MapActionsConfig) => void
}

type RowContentProps = {
  item: MapActionConfigItem
  position: number
  onEdit?: () => void
  onStateChange?: (state: MapActionState) => void
  dragHandleProps?: Record<string, unknown>
}

const RowContent = ({ item, position, onEdit, onStateChange, dragHandleProps }: RowContentProps) => (
  <>
    <button
      type="button"
      className="map-filter-drag-handle"
      aria-label={`Drag to reorder ${item.label}`}
      disabled={!onEdit}
      {...dragHandleProps}
    >
      <Icon name="GripVertical" className="map-filter-drag-icon" />
      <span className="map-filter-position-label">{position + 1}</span>
    </button>
    <span className={`map-action-tile${item.state === MapActionState.Disabled ? ' is-disabled' : ''}`}>
      <MapActionIcon item={item} />
    </span>
    <div className="map-action-row__main">
      <div className="map-action-row__title">
        {item.label}
        <code>{item.key}</code>
        {item.iconUrl ? (
          <span className="map-action-row__tag">custom icon</span>
        ) : item.iconName ? (
          <span className="map-action-row__tag">{item.iconName}</span>
        ) : null}
      </div>
      <div className="map-action-row__description">{MAP_ACTION_DESCRIPTIONS[item.key]}</div>
    </div>
    <div className="map-action-row__pill">
      <MapActionCenterPill item={item} />
    </div>
    <select
      className={`map-filter-select map-action-state-select map-action-state--${item.state}`}
      value={item.state}
      disabled={!onStateChange}
      aria-label={`${item.label} availability`}
      onChange={e => onStateChange?.(e.target.value as MapActionState)}
    >
      {MAP_ACTION_STATE_OPTIONS.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <button type="button" className="map-action-row__edit" onClick={onEdit} disabled={!onEdit}>
      <Icon name="Pen" />
      Edit
    </button>
  </>
)

const SortableRow = (props: Omit<RowContentProps, 'dragHandleProps'>) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.item.key })
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={[
        'map-action-row',
        `map-action-row--${props.item.state}`,
        isDragging ? 'map-filter-row-dragging' : '',
      ].join(' ')}
    >
      <RowContent {...props} dragHandleProps={{ ...attributes, ...listeners }} />
    </li>
  )
}

/**
 * New-map sidebar filters: drag to reorder (top of the list = top of the
 * sidebar), quick availability switch per row, everything else in the edit
 * modal. The phone preview reflects unsaved changes.
 */
const MapActionsEditor = ({ value, onChange }: MapActionsEditorProps) => {
  const [activeKey, setActiveKey] = useState<MapActionKey | null>(null)
  const [editingKey, setEditingKey] = useState<MapActionKey | null>(null)
  const [previewKey, setPreviewKey] = useState<MapActionKey | null>(MapActionKey.Tickets)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const updateItem = (key: MapActionKey, changes: Partial<MapActionConfigItem>) =>
    onChange(value.map(item => (item.key === key ? { ...item, ...changes } : item)))

  const handleDragStart = (event: DragStartEvent) => setActiveKey(event.active.id as MapActionKey)

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveKey(null)
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = value.findIndex(item => item.key === active.id)
    const newIndex = value.findIndex(item => item.key === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    onChange(arrayMove(value, oldIndex, newIndex))
  }

  const activeIndex = value.findIndex(item => item.key === activeKey)
  const editingItem = value.find(item => item.key === editingKey) ?? null

  return (
    <div className="map-actions-editor">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveKey(null)}
      >
        <SortableContext items={value.map(item => item.key)} strategy={verticalListSortingStrategy}>
          <ol className="map-action-list">
            {value.map((item, index) => (
              <SortableRow
                key={item.key}
                item={item}
                position={index}
                onEdit={() => setEditingKey(item.key)}
                onStateChange={state => updateItem(item.key, { state })}
              />
            ))}
          </ol>
        </SortableContext>
        <DragOverlay dropAnimation={null}>
          {activeIndex >= 0 ? (
            <div className="map-action-row map-action-row--overlay">
              <RowContent item={value[activeIndex]} position={activeIndex} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <MapActionsPhonePreview items={value} selectedKey={previewKey} onSelect={setPreviewKey} />

      <MapActionEditModal
        isOpen={editingItem != null}
        item={editingItem}
        onClose={() => setEditingKey(null)}
        onApply={item => updateItem(item.key, item)}
      />
    </div>
  )
}

export default MapActionsEditor
